import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildClientDisplayName,
  inferMarginPreset,
  SERVICE_CONTRACT_MODEL_OPTIONS,
  SERVICE_MARGIN_PRESET_OPTIONS,
  SERVICE_TEMPLATE_FREQUENCY_OPTIONS,
  SERVICE_MODE_OPTIONS,
  SERVICE_PRICING_RULE_OPTIONS,
  SERVICE_PRIORITY_OPTIONS,
  resolveMarginPresetPercent,
  SERVICE_STOP_ACTION_OPTIONS,
  SERVICE_TRANSPORT_OPTIONS,
  resolveServiceTaskLabel,
} from '../serviceOptions';
import {
  buildLocationSnapshotFromManual,
  buildLocationSnapshotFromOption,
  buildServiceLocationOptions,
  createManualLocationState,
} from '../serviceLocationUtils';
import { estimateServiceCommercialMetrics } from '../servicePricingEstimate';

const Icon = ({ path, className = 'w-5 h-5' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const EMPTY_FORM = {
  clientMode: 'existing',
  clientId: '',
  newClientName: '',
  newClientContactName: '',
  newClientPhone: '',
  newClientEmail: '',
  serviceMode: 'puntual',
  serviceTransport: 'camioneta',
  frequency: 'once',
  saveAsTemplate: false,
  scheduledDate: '',
  commercialModel: 'trip_rate',
  pricingRule: 'percentage_margin',
  marginPreset: 'normal',
  agreedPricePerTrip: '',
  fixedMarginAmount: '',
  marginPercent: '40',
  useClientPricing: true,
  priority: 'normal',
  notes: '',
};

const GEOAPIFY_API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY || '';

const baseFieldClassName = 'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100';
const readOnlyFieldClassName = `${baseFieldClassName} bg-slate-50 text-slate-600`;

const hasCoordinates = (state = {}) => {
  const lat = Number(state?.lat);
  const lng = Number(state?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng);
};

const InputBlock = ({ label, children, helper = '' }) => (
  <label className="block space-y-2">
    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
    {children}
    {helper ? <span className="block text-xs text-slate-400">{helper}</span> : null}
  </label>
);

const CONTACT_TYPE_OPTIONS = [
  { value: 'cliente', label: 'Cliente' },
  { value: 'proveedor', label: 'Proveedor' },
  { value: 'transporte', label: 'Transporte' },
];

const SectionFrame = ({ eyebrow, title, description, tone = 'slate', children }) => {
  const toneMap = {
    slate: {
      shell: 'border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] shadow-[0_16px_36px_rgba(15,23,42,0.06)]',
      badge: 'border-slate-200 bg-slate-50 text-slate-700',
    },
    cyan: {
      shell: 'border-cyan-100 bg-[linear-gradient(180deg,#f8fdff_0%,#ffffff_100%)] shadow-[0_16px_36px_rgba(8,145,178,0.07)]',
      badge: 'border-cyan-200 bg-cyan-50 text-cyan-800',
    },
    violet: {
      shell: 'border-violet-100 bg-[linear-gradient(180deg,#faf5ff_0%,#ffffff_100%)] shadow-[0_16px_36px_rgba(124,58,237,0.07)]',
      badge: 'border-violet-200 bg-violet-50 text-violet-800',
    },
    emerald: {
      shell: 'border-emerald-100 bg-[linear-gradient(180deg,#f0fdf4_0%,#ffffff_100%)] shadow-[0_16px_36px_rgba(16,185,129,0.06)]',
      badge: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    },
  };

  const currentTone = toneMap[tone] || toneMap.slate;

  return (
    <section className={`rounded-[30px] border p-5 ${currentTone.shell}`}>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4 border-b border-white/70 pb-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">{eyebrow}</p>
          <h3 className="mt-2 text-xl font-black tracking-tight text-slate-950">{title}</h3>
          <p className="mt-1 text-sm leading-6 text-slate-500">{description}</p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${currentTone.badge}`}>
          {eyebrow}
        </span>
      </div>
      {children}
    </section>
  );
};

const contractAccentByModel = {
  trip_rate: {
    shell: 'border-cyan-200 bg-[linear-gradient(135deg,#ecfeff_0%,#ffffff_100%)]',
    badge: 'border-cyan-200 bg-cyan-50 text-cyan-800',
    glow: 'bg-cyan-200/40',
  },
  monthly_fixed: {
    shell: 'border-violet-200 bg-[linear-gradient(135deg,#f5f3ff_0%,#ffffff_100%)]',
    badge: 'border-violet-200 bg-violet-50 text-violet-800',
    glow: 'bg-violet-200/40',
  },
  monthly_variable: {
    shell: 'border-emerald-200 bg-[linear-gradient(135deg,#ecfdf5_0%,#ffffff_100%)]',
    badge: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    glow: 'bg-emerald-200/40',
  },
  none: {
    shell: 'border-amber-200 bg-[linear-gradient(135deg,#fffbeb_0%,#ffffff_100%)]',
    badge: 'border-amber-200 bg-amber-50 text-amber-800',
    glow: 'bg-amber-200/40',
  },
};

const createAdditionalStopState = () => ({
  id: `stop-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  ...createManualLocationState(),
  serviceAction: 'entrega',
  actionNotes: '',
});

const createDestinationState = () => ({
  ...createManualLocationState(),
  serviceAction: 'entrega',
  actionNotes: '',
});

const createClientAddressState = () => ({
  ...createManualLocationState(),
  mode: 'manual',
  saveToSharedBase: false,
});

const withStopMetadata = (snapshot = null, state = {}, fallbackAction = 'entrega') => {
  if (!snapshot) return null;
  const action = String(state?.serviceAction || snapshot?.serviceAction || fallbackAction).trim() || fallbackAction;
  return {
    ...snapshot,
    serviceAction: action,
    serviceActionLabel: resolveServiceTaskLabel(action),
    actionNotes: String(state?.actionNotes || snapshot?.actionNotes || '').trim(),
  };
};

const summarizeStopActions = (destinationSnapshot = null, additionalStops = []) => {
  const allStops = [destinationSnapshot, ...(Array.isArray(additionalStops) ? additionalStops : [])].filter(Boolean);
  if (allStops.length === 0) {
    return { serviceTask: 'servicio', serviceTaskLabel: 'Servicio' };
  }
  if (allStops.length === 1) {
    const action = String(allStops[0]?.serviceAction || 'entrega').trim() || 'entrega';
    return {
      serviceTask: action,
      serviceTaskLabel: allStops[0]?.serviceActionLabel || resolveServiceTaskLabel(action),
    };
  }
  return {
    serviceTask: 'multi_stop',
    serviceTaskLabel: `${allStops.length} paradas`,
  };
};

const StopActionFields = ({ state, onChange, title = 'Acción en este punto' }) => (
  <div className="mt-4 rounded-[22px] border border-slate-200 bg-white/85 p-4 shadow-sm">
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <InputBlock label={title} helper="Definí qué se hace exactamente en este destino.">
        <select
          value={state.serviceAction || 'entrega'}
          onChange={(event) => onChange((current) => ({ ...current, serviceAction: event.target.value }))}
          className={baseFieldClassName}
        >
          {SERVICE_STOP_ACTION_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </InputBlock>
      <InputBlock label="Observación de la parada" helper="Contacto, firma, sobre, cobranza o detalle útil para el chofer.">
        <textarea
          value={state.actionNotes || ''}
          onChange={(event) => onChange((current) => ({ ...current, actionNotes: event.target.value }))}
          className={`${baseFieldClassName} min-h-[110px] resize-y`}
          placeholder="Ejemplo: retirar sobre cerrado, cobrar efectivo, entregar con firma."
        />
      </InputBlock>
    </div>
  </div>
);

const buildExistingLocationState = (option = null) => ({
  ...createManualLocationState(),
  mode: 'existing',
  optionId: option?.id || '',
  label: option?.label || '',
  address: option?.address || '',
  locality: option?.locality || '',
  province: option?.province || '',
  postalCode: option?.postalCode || '',
  lat: option?.latlng?.lat ?? '',
  lng: option?.latlng?.lng ?? '',
  serviceAction: option?.serviceAction || 'entrega',
  actionNotes: option?.actionNotes || '',
});

const EndpointSection = ({
  title,
  state,
  onChange,
  locationOptions,
  tone = 'slate',
  description = 'Podés elegir un destino ya guardado o cargar uno nuevo con dirección y coordenadas.',
  badgeLabel = '',
  existingEmptyText = 'Elegí una dirección ya registrada de clientes, contactos o destinos guardados de servicio.',
  addressHelper = '',
  hideSharedBaseToggle = false,
  hideContactType = false,
}) => {
  const autocompleteWrapRef = useRef(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [loadingAddressSuggestions, setLoadingAddressSuggestions] = useState(false);
  const selectedOption = locationOptions.find((option) => option.id === state.optionId) || null;
  const geolocationReady = state.mode === 'existing'
    ? Boolean(selectedOption?.latlng && Number.isFinite(Number(selectedOption.latlng.lat)) && Number.isFinite(Number(selectedOption.latlng.lng)))
    : hasCoordinates(state);
  const resolvedLabel = state.mode === 'existing' ? (selectedOption?.label || '') : state.label;
  const resolvedAddress = state.mode === 'existing' ? (selectedOption?.address || '') : state.address;
  const resolvedLocality = state.mode === 'existing' ? (selectedOption?.locality || '') : state.locality;
  const resolvedProvince = state.mode === 'existing' ? (selectedOption?.province || '') : state.province;
  const resolvedPostalCode = state.mode === 'existing' ? (selectedOption?.postalCode || '') : state.postalCode;
  const resolvedLat = state.mode === 'existing' ? (selectedOption?.latlng?.lat ?? '') : state.lat;
  const resolvedLng = state.mode === 'existing' ? (selectedOption?.latlng?.lng ?? '') : state.lng;
  const toneClasses = tone === 'origin'
    ? {
        shell: 'border-cyan-200 bg-[linear-gradient(135deg,#ecfeff_0%,#f8fafc_100%)]',
        badge: 'border-cyan-200 bg-cyan-50 text-cyan-800',
      }
    : tone === 'client'
      ? {
          shell: 'border-emerald-200 bg-[linear-gradient(135deg,#ecfdf5_0%,#ffffff_100%)]',
          badge: 'border-emerald-200 bg-emerald-50 text-emerald-800',
        }
      : {
          shell: 'border-violet-200 bg-[linear-gradient(135deg,#faf5ff_0%,#fff7ed_100%)]',
          badge: 'border-violet-200 bg-violet-50 text-violet-800',
        };

  useEffect(() => {
    if (state.mode !== 'manual' || !GEOAPIFY_API_KEY) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      setLoadingAddressSuggestions(false);
      return undefined;
    }

    const queryText = String(state.address || '').trim();
    const localityText = String(state.locality || '').trim();
    const provinceText = String(state.province || '').trim();
    const shouldQuery = queryText.length >= 3;

    if (!shouldQuery) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      setLoadingAddressSuggestions(false);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        setLoadingAddressSuggestions(true);
        const searchText = [queryText, localityText, provinceText].filter(Boolean).join(', ');
        const url =
          `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(searchText)}` +
          `&lang=es&limit=5&filter=countrycode:ar&format=json&apiKey=${GEOAPIFY_API_KEY}`;
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Geoapify autocomplete error: ${response.status}`);
        const data = await response.json();
        const results = Array.isArray(data?.results) ? data.results : [];
        setAddressSuggestions(results);
        setShowAddressSuggestions(true);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error cargando sugerencias de dirección en Servicio:', error);
          setAddressSuggestions([]);
          setShowAddressSuggestions(false);
        }
      } finally {
        setLoadingAddressSuggestions(false);
      }
    }, 350);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [state.mode, state.address, state.locality, state.province]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!autocompleteWrapRef.current?.contains(event.target)) {
        setShowAddressSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`rounded-[26px] border p-5 ${toneClasses.shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-black tracking-tight text-slate-900">{title}</p>
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${toneClasses.badge}`}>
          {badgeLabel || (tone === 'origin' ? 'Punto de salida' : tone === 'client' ? 'Ficha del cliente' : 'Punto operativo')}
        </span>
        <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
          <button
            type="button"
            onClick={() => onChange((current) => ({ ...current, mode: 'existing' }))}
            className={`rounded-2xl px-3 py-2 text-xs font-semibold transition ${state.mode === 'existing' ? 'bg-cyan-50 text-cyan-800' : 'text-slate-500 hover:bg-slate-50'}`}
          >
            Buscar existente
          </button>
          <button
            type="button"
            onClick={() => onChange((current) => ({ ...current, mode: 'manual' }))}
            className={`rounded-2xl px-3 py-2 text-xs font-semibold transition ${state.mode === 'manual' ? 'bg-violet-50 text-violet-800' : 'text-slate-500 hover:bg-slate-50'}`}
          >
            Cargar nuevo
          </button>
        </div>
      </div>

      {state.mode === 'existing' ? (
        <div className="mt-4 space-y-3">
          <select
            value={state.optionId}
              onChange={(event) => {
                const nextOptionId = event.target.value;
                const option = locationOptions.find((entry) => entry.id === nextOptionId) || null;
                onChange((current) => ({
                  ...current,
                  optionId: nextOptionId,
                  label: option?.label || '',
                  address: option?.address || '',
                  locality: option?.locality || '',
                  province: option?.province || '',
                  postalCode: option?.postalCode || '',
                  lat: option?.latlng?.lat ?? '',
                  lng: option?.latlng?.lng ?? '',
                }));
              }}
            className={baseFieldClassName}
          >
            <option value="">Elegí una dirección existente</option>
            {locationOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label} · {option.address}
              </option>
            ))}
          </select>

          <div className="rounded-[20px] border border-slate-200 bg-white px-4 py-3 text-sm">
            {selectedOption ? (
              <div className="space-y-1 text-slate-700">
                <p><span className="font-semibold text-slate-900">Referencia:</span> {selectedOption.label}</p>
                <p><span className="font-semibold text-slate-900">Dirección:</span> {selectedOption.address}</p>
                <p><span className="font-semibold text-slate-900">Coordenadas:</span> {selectedOption.latlng ? `Lat ${Number(selectedOption.latlng.lat).toFixed(5)} · Lng ${Number(selectedOption.latlng.lng).toFixed(5)}` : 'Todavía sin coordenadas guardadas'}</p>
              </div>
            ) : (
              <p className="text-slate-500">{existingEmptyText}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <InputBlock label="Nombre de referencia" helper="Ejemplo: Retiro cliente, depósito externo o entrega centro.">
            <input value={state.label} onChange={(event) => onChange((current) => ({ ...current, label: event.target.value }))} className={baseFieldClassName} placeholder="Nombre corto del punto" />
          </InputBlock>
          <div className="md:col-span-2" ref={autocompleteWrapRef}>
            <InputBlock label="Dirección" helper={addressHelper || (!GEOAPIFY_API_KEY ? 'Falta configurar Geoapify para ver sugerencias automáticas.' : 'Escribí calle y número para ver calles sugeridas.')}>
              <div className="relative">
                <input
                  value={state.address}
                  onChange={(event) => onChange((current) => ({ ...current, address: event.target.value }))}
                  onFocus={() => {
                    if (addressSuggestions.length > 0) {
                      setShowAddressSuggestions(true);
                    }
                  }}
                  autoComplete="off"
                  className={`${baseFieldClassName} pr-12`}
                  placeholder="Calle y número"
                />
                {loadingAddressSuggestions ? (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-cyan-600">
                    <Icon
                      path="M4.75 12a7.25 7.25 0 0112.511-4.95M19.25 12a7.25 7.25 0 01-12.511 4.95"
                      className="h-5 w-5 animate-spin"
                    />
                  </div>
                ) : null}
              </div>

              {showAddressSuggestions ? (
                <div className="mt-2 overflow-hidden rounded-2xl border border-cyan-200 bg-white shadow-[0_18px_45px_rgba(6,182,212,0.14)]">
                  {addressSuggestions.length > 0 ? (
                    <div className="max-h-64 overflow-y-auto p-2">
                      {addressSuggestions.map((item, index) => (
                        <button
                          key={`${item.place_id || item.formatted || 'suggestion'}-${index}`}
                          type="button"
                          onClick={() => {
                            onChange((current) => ({
                              ...current,
                              address: item.address_line1 || item.street || item.formatted || current.address,
                              locality: item.city || item.county || item.state_district || current.locality,
                              province: item.state || current.province,
                              postalCode: item.postcode || current.postalCode,
                              lat: item.lat ?? current.lat,
                              lng: item.lon ?? current.lng,
                              label: current.label || item.address_line1 || item.street || 'Destino',
                            }));
                            setAddressSuggestions([]);
                            setShowAddressSuggestions(false);
                          }}
                          className="flex w-full flex-col rounded-2xl px-3 py-3 text-left transition hover:bg-cyan-50"
                        >
                          <p className="font-semibold text-slate-900">
                            {item.address_line1 || item.street || item.formatted || 'Dirección sugerida'}
                          </p>
                          <p className="mt-1 text-sm text-slate-500">
                            {item.formatted || [item.city, item.state, item.country].filter(Boolean).join(', ')}
                          </p>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-3 text-sm text-slate-600">
                      No encontramos sugerencias todavía. Probá con calle, número y localidad.
                    </div>
                  )}
                </div>
              ) : null}
            </InputBlock>
          </div>
          <InputBlock label="Localidad">
            <input value={state.locality} onChange={(event) => onChange((current) => ({ ...current, locality: event.target.value }))} className={baseFieldClassName} placeholder="Localidad" />
          </InputBlock>
          <InputBlock label="Provincia">
            <input value={state.province} onChange={(event) => onChange((current) => ({ ...current, province: event.target.value }))} className={baseFieldClassName} placeholder="Provincia" />
          </InputBlock>
          <InputBlock label="Código postal">
            <input value={state.postalCode} onChange={(event) => onChange((current) => ({ ...current, postalCode: event.target.value }))} className={baseFieldClassName} placeholder="Código postal" />
          </InputBlock>
          <InputBlock label="Latitud" helper="Opcional, pero ayuda para usarlo luego en logística y mapa.">
            <input value={state.lat} onChange={(event) => onChange((current) => ({ ...current, lat: event.target.value }))} className={baseFieldClassName} placeholder="-34.6037" />
          </InputBlock>
          <InputBlock label="Longitud">
            <input value={state.lng} onChange={(event) => onChange((current) => ({ ...current, lng: event.target.value }))} className={baseFieldClassName} placeholder="-58.3816" />
          </InputBlock>
          {!hideSharedBaseToggle ? (
            <label className="md:col-span-2 inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
              <input
                type="checkbox"
                checked={state.saveToSharedBase}
                onChange={(event) => onChange((current) => ({ ...current, saveToSharedBase: event.target.checked }))}
                className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
              />
              Guardar este punto en la base común para reutilizarlo en próximos servicios
            </label>
          ) : null}
        </div>
      )}

      <div className="mt-4 rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Validación de ubicación</p>
          <span
            className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${
              geolocationReady
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-amber-200 bg-amber-50 text-amber-700'
            }`}
          >
            {geolocationReady ? 'Geolocalización lista' : 'Se validará al guardar'}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <InputBlock label="Referencia visible">
            <input value={resolvedLabel} readOnly className={readOnlyFieldClassName} placeholder="Todavía sin referencia" />
          </InputBlock>
          <InputBlock label="Dirección usada">
            <input value={resolvedAddress} readOnly className={readOnlyFieldClassName} placeholder="Todavía sin dirección" />
          </InputBlock>
          <InputBlock label="Localidad">
            <input value={resolvedLocality} readOnly className={readOnlyFieldClassName} placeholder="Sin localidad" />
          </InputBlock>
          <InputBlock label="Provincia">
            <input value={resolvedProvince} readOnly className={readOnlyFieldClassName} placeholder="Sin provincia" />
          </InputBlock>
          <InputBlock label="Código postal">
              <input value={resolvedPostalCode} readOnly className={readOnlyFieldClassName} placeholder="Sin código postal" />
            </InputBlock>
            {state.mode === 'manual' && !hideContactType ? (
              <InputBlock label="Guardar como" helper="Así lo derivamos a la base global correcta para reutilizarlo en toda la app.">
                <select
                  value={state.contactType || 'cliente'}
                  onChange={(event) => onChange((current) => ({ ...current, contactType: event.target.value }))}
                  className={baseFieldClassName}
                >
                  {CONTACT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </InputBlock>
            ) : null}
            <div className="grid grid-cols-2 gap-4">
            <InputBlock label="Latitud">
              <input value={resolvedLat} readOnly className={readOnlyFieldClassName} placeholder="Se completa al validar" />
            </InputBlock>
            <InputBlock label="Longitud">
              <input value={resolvedLng} readOnly className={readOnlyFieldClassName} placeholder="Se completa al validar" />
            </InputBlock>
          </div>
        </div>
      </div>
    </div>
  );
};

const ServiceCreateModal = ({
  open,
  clients = [],
  locations = [],
  contractsByClientId = new Map(),
  liquidationSettings = {},
  defaultOperationalOrigin = null,
  saving = false,
  onClose,
  onSubmit,
  onCreateClient = null,
  onOpenClientContractEditor = null,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [clientSearch, setClientSearch] = useState('');
  const [clientAddressState, setClientAddressState] = useState(createClientAddressState());
  const [originState, setOriginState] = useState(createManualLocationState());
  const [destinationState, setDestinationState] = useState(createDestinationState());
  const [additionalStops, setAdditionalStops] = useState([]);
  const [savingClientDraft, setSavingClientDraft] = useState(false);
  const [persistedClientOverride, setPersistedClientOverride] = useState(null);

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setClientSearch('');
      setClientAddressState(createClientAddressState());
      setOriginState(defaultOperationalOrigin ? buildExistingLocationState(defaultOperationalOrigin) : createManualLocationState());
      setDestinationState(createDestinationState());
      setAdditionalStops([]);
      setSavingClientDraft(false);
      setPersistedClientOverride(null);
      return;
    }

    const now = new Date();
    const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    setForm((current) => ({ ...EMPTY_FORM, scheduledDate: current.scheduledDate || localDate }));
    setClientSearch('');
    setClientAddressState(createClientAddressState());
    setOriginState(defaultOperationalOrigin ? buildExistingLocationState(defaultOperationalOrigin) : createManualLocationState());
    setDestinationState(createDestinationState());
    setAdditionalStops([]);
    setSavingClientDraft(false);
    setPersistedClientOverride(null);
  }, [defaultOperationalOrigin, open]);

  const availableClients = useMemo(() => {
    if (!persistedClientOverride?.id) return clients;
    return clients.some((client) => client.id === persistedClientOverride.id)
      ? clients
      : [...clients, persistedClientOverride];
  }, [clients, persistedClientOverride]);

  const sortedClients = useMemo(
    () => [...availableClients].sort((a, b) => buildClientDisplayName(a).localeCompare(buildClientDisplayName(b), 'es')),
    [availableClients]
  );

  const locationOptions = useMemo(() => {
    const nextOptions = buildServiceLocationOptions({ contacts: availableClients, sharedLocations: locations });
    if (!defaultOperationalOrigin?.address) return nextOptions;

    const alreadyIncluded = nextOptions.some((option) => option.id === defaultOperationalOrigin.id);
    return alreadyIncluded ? nextOptions : [defaultOperationalOrigin, ...nextOptions];
  }, [availableClients, defaultOperationalOrigin, locations]);
  const selectedClient =
    sortedClients.find((client) => client.id === form.clientId) ||
    (persistedClientOverride?.id && persistedClientOverride.id === form.clientId ? persistedClientOverride : null);
  const isCreatingClient = form.clientMode === 'new';
  const selectedContract = !isCreatingClient && selectedClient
    ? contractsByClientId.get(String(selectedClient.id || '').trim()) || null
    : null;
  const usesMonthlyFixedContract = selectedContract?.commercialModel === 'monthly_fixed';
  const effectiveCommercialModel = form.useClientPricing && selectedContract?.commercialModel
    ? selectedContract.commercialModel
    : form.commercialModel;
  const effectivePricingRule = form.useClientPricing && selectedContract?.pricingRule
    ? selectedContract.pricingRule
    : form.pricingRule;
  const effectiveMarginPreset = form.useClientPricing && selectedContract?.pricingRule === 'percentage_margin'
    ? selectedContract?.marginPreset || inferMarginPreset(selectedContract?.marginPercent)
    : form.marginPreset;
  const isRecurring = form.frequency && form.frequency !== 'once';
  const isPunctualService = form.serviceMode === 'puntual';

  const handleChange = (field) => (event) => {
    const value = event?.target?.value ?? '';
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'frequency' && value === 'once') {
        next.saveAsTemplate = false;
      }
      if (field === 'frequency' && value !== 'once' && current.saveAsTemplate === false) {
        next.saveAsTemplate = true;
      }
      return next;
    });
  };

  const handleMarginPresetChange = (event) => {
    const nextPreset = event?.target?.value || 'normal';
    setForm((current) => ({
      ...current,
      marginPreset: nextPreset,
      useClientPricing: false,
      marginPercent:
        nextPreset === 'manual'
          ? current.marginPercent
          : String(resolveMarginPresetPercent(nextPreset)),
    }));
  };

  useEffect(() => {
    if (!open) return;

    setForm((current) => {
      const next = { ...current };
      if (usesMonthlyFixedContract && current.useClientPricing) {
        next.commercialModel = 'monthly_fixed';
      }
      if (selectedContract?.pricingRule && current.useClientPricing) {
        next.pricingRule = selectedContract.pricingRule;
      }
      if (current.useClientPricing && selectedContract?.pricingRule === 'percentage_margin') {
        next.marginPreset = selectedContract?.marginPreset || inferMarginPreset(selectedContract?.marginPercent);
      }
      if (
        current.useClientPricing &&
        selectedContract?.pricingRule === 'percentage_margin' &&
        (Number(selectedContract?.marginPercent || 0) > 0 || selectedContract?.marginPreset)
      ) {
        next.marginPercent = String(
          Number(selectedContract?.marginPercent || 0) > 0
            ? Number(selectedContract.marginPercent || 0)
            : resolveMarginPresetPercent(selectedContract?.marginPreset || inferMarginPreset(selectedContract?.marginPercent))
        );
      }
      if (
        current.useClientPricing &&
        selectedContract?.pricingRule === 'fixed_margin' &&
        Number(selectedContract?.fixedMarginAmount || 0) > 0
      ) {
        next.fixedMarginAmount = String(Number(selectedContract.fixedMarginAmount || 0));
      }
      if (
        current.useClientPricing &&
        selectedContract?.pricingRule === 'manual_total' &&
        Number(selectedContract?.agreedPricePerTrip || 0) > 0
      ) {
        next.agreedPricePerTrip = String(Number(selectedContract.agreedPricePerTrip || 0));
      }
      return next;
    });
  }, [open, selectedContract, usesMonthlyFixedContract]);

  useEffect(() => {
    if (!open || !isPunctualService) return;

    setForm((current) => {
      const next = { ...current };
      let changed = false;

      if (current.frequency !== 'once') {
        next.frequency = 'once';
        changed = true;
      }
      if (current.saveAsTemplate) {
        next.saveAsTemplate = false;
        changed = true;
      }
      if (current.commercialModel !== 'trip_rate') {
        next.commercialModel = 'trip_rate';
        changed = true;
      }

      return changed ? next : current;
    });
  }, [isPunctualService, open]);

  useEffect(() => {
    if (!open || isCreatingClient) return;
    if (selectedClient) {
      setClientSearch(buildClientDisplayName(selectedClient));
    }
  }, [isCreatingClient, open, selectedClient]);

  const previewOriginSnapshot = useMemo(
    () =>
      originState.mode === 'existing'
        ? buildLocationSnapshotFromOption(locationOptions.find((option) => option.id === originState.optionId) || null)
        : buildLocationSnapshotFromManual(originState),
    [locationOptions, originState]
  );

  const previewDestinationSnapshot = useMemo(
    () => {
      const baseSnapshot =
        destinationState.mode === 'existing'
          ? buildLocationSnapshotFromOption(locationOptions.find((option) => option.id === destinationState.optionId) || null)
          : buildLocationSnapshotFromManual(destinationState);
      return withStopMetadata(baseSnapshot, destinationState, 'entrega');
    },
    [locationOptions, destinationState]
  );

  const previewAdditionalStops = useMemo(
    () =>
      additionalStops
        .map((stop) => {
          const baseSnapshot =
            stop.mode === 'existing'
              ? buildLocationSnapshotFromOption(locationOptions.find((option) => option.id === stop.optionId) || null)
              : buildLocationSnapshotFromManual(stop);
          return withStopMetadata(baseSnapshot, stop, 'entrega');
        })
        .filter((stop) => stop?.address),
    [additionalStops, locationOptions]
  );

  const previewClientAddressSnapshot = useMemo(
    () =>
      clientAddressState.mode === 'existing'
        ? buildLocationSnapshotFromOption(locationOptions.find((option) => option.id === clientAddressState.optionId) || null)
        : buildLocationSnapshotFromManual(clientAddressState),
    [clientAddressState, locationOptions]
  );

  const clientProfileReady = isCreatingClient
    ? Boolean(String(form.newClientName || '').trim() && previewClientAddressSnapshot?.address)
    : Boolean(selectedClient);

  const commercialPreview = useMemo(() => {
    const baseService = {
      serviceTransport: form.serviceTransport,
      originSnapshot: previewOriginSnapshot,
      destinationSnapshot: previewDestinationSnapshot,
      additionalStops: previewAdditionalStops,
      commercialModel: effectiveCommercialModel,
      pricingRule: effectivePricingRule,
      monthlyFeeSnapshot: form.useClientPricing ? Number(selectedContract?.monthlyFee || 0) || 0 : Number(form.monthlyFee || 0) || 0,
      agreedPricePerTripSnapshot:
        effectivePricingRule === 'manual_total'
          ? (form.useClientPricing ? Number(selectedContract?.agreedPricePerTrip || 0) || 0 : Number(form.agreedPricePerTrip || 0) || 0)
          : 0,
      fixedMarginAmountSnapshot:
        effectivePricingRule === 'fixed_margin'
          ? (form.useClientPricing ? Number(selectedContract?.fixedMarginAmount || 0) || 0 : Number(form.fixedMarginAmount || 0) || 0)
          : 0,
      marginPercentSnapshot:
        effectivePricingRule === 'percentage_margin'
          ? (form.useClientPricing
              ? Number(selectedContract?.marginPercent || 0) || resolveMarginPresetPercent(selectedContract?.marginPreset || inferMarginPreset(selectedContract?.marginPercent))
              : effectiveMarginPreset === 'manual'
                ? Number(form.marginPercent || 0) || 0
                : resolveMarginPresetPercent(effectiveMarginPreset))
          : 0,
    };

    return estimateServiceCommercialMetrics({
      service: baseService,
      liquidationSettings,
    });
  }, [
    effectiveCommercialModel,
    effectiveMarginPreset,
    effectivePricingRule,
    form.agreedPricePerTrip,
    form.fixedMarginAmount,
    form.marginPreset,
    form.marginPercent,
    form.monthlyFee,
    form.serviceTransport,
    form.useClientPricing,
    liquidationSettings,
    previewAdditionalStops,
    previewDestinationSnapshot,
    previewOriginSnapshot,
    selectedContract,
  ]);

  const activeCommercialModelMeta = useMemo(
    () => SERVICE_CONTRACT_MODEL_OPTIONS.find((option) => option.value === effectiveCommercialModel) || null,
    [effectiveCommercialModel]
  );
  const activePricingRuleMeta = useMemo(
    () => SERVICE_PRICING_RULE_OPTIONS.find((option) => option.value === effectivePricingRule) || null,
    [effectivePricingRule]
  );
  const clientStats = useMemo(() => {
    let withContract = 0;
    let withoutContract = 0;
    sortedClients.forEach((client) => {
      const hasContract = Boolean(contractsByClientId.get(String(client?.id || '').trim()));
      if (hasContract) withContract += 1;
      else withoutContract += 1;
    });
    return { withContract, withoutContract };
  }, [contractsByClientId, sortedClients]);
  const selectedClientAccent = selectedContract
    ? contractAccentByModel[selectedContract?.commercialModel] || contractAccentByModel.monthly_fixed
    : contractAccentByModel.none;
  const filteredClients = useMemo(() => {
    const query = String(clientSearch || '').trim().toLowerCase();
    if (!query) return sortedClients.slice(0, 8);

    return sortedClients
      .filter((client) => {
        const display = buildClientDisplayName(client).toLowerCase();
        const contact = String(client?.contactName || '').toLowerCase();
        const phone = String(client?.phone || '').toLowerCase();
        return display.includes(query) || contact.includes(query) || phone.includes(query);
      })
      .slice(0, 8);
  }, [clientSearch, sortedClients]);
  const existingClientPanelClassName = selectedClient
    ? `rounded-[24px] border p-4 shadow-sm ${selectedClientAccent.shell}`
    : 'rounded-[24px] border border-slate-200 bg-[linear-gradient(135deg,#f8fafc_0%,#ffffff_100%)] p-4 shadow-sm';
  const clientContractLabel = selectedContract
    ? SERVICE_CONTRACT_MODEL_OPTIONS.find((option) => option.value === selectedContract?.commercialModel)?.label || 'Contrato'
    : 'Sin contrato';
  const selectedContractRuleLabel = selectedContract
    ? SERVICE_PRICING_RULE_OPTIONS.find((option) => option.value === selectedContract?.pricingRule)?.label || 'Regla comercial'
    : 'Regla puntual';
  const buildClientContractMeta = (client) => {
    const contract = contractsByClientId.get(String(client?.id || '').trim()) || null;
    const accent = contract
      ? contractAccentByModel[contract?.commercialModel] || contractAccentByModel.monthly_fixed
      : contractAccentByModel.none;
    const label = contract
      ? SERVICE_CONTRACT_MODEL_OPTIONS.find((option) => option.value === contract?.commercialModel)?.label || 'Contrato'
      : 'Sin contrato';
    return { contract, accent, label };
  };

  if (!open) return null;

  const handlePersistClient = async () => {
    if (savingClientDraft || typeof onCreateClient !== 'function') return;
    if (!String(form.newClientName || '').trim() || !previewClientAddressSnapshot?.address) return;

    setSavingClientDraft(true);
    try {
      const createdClient = await onCreateClient({
        newClientName: form.newClientName,
        newClientContactName: form.newClientContactName,
        newClientPhone: form.newClientPhone,
        newClientEmail: form.newClientEmail,
        newClientAddressSnapshot: previewClientAddressSnapshot,
      });

      if (createdClient?.id) {
        const nextLabel = buildClientDisplayName(createdClient);
        setPersistedClientOverride(createdClient);
        setForm((current) => ({
          ...current,
          clientMode: 'existing',
          clientId: createdClient.id,
          newClientName: '',
          newClientContactName: '',
          newClientPhone: '',
          newClientEmail: '',
        }));
        setClientSearch(nextLabel);
        setClientAddressState(createClientAddressState());
      }
    } finally {
      setSavingClientDraft(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (!clientProfileReady) return;

    const originSnapshot = originState.mode === 'existing'
      ? buildLocationSnapshotFromOption(locationOptions.find((option) => option.id === originState.optionId) || null)
      : buildLocationSnapshotFromManual(originState);

    const destinationSnapshot = destinationState.mode === 'existing'
      ? withStopMetadata(buildLocationSnapshotFromOption(locationOptions.find((option) => option.id === destinationState.optionId) || null), destinationState, 'entrega')
      : withStopMetadata(buildLocationSnapshotFromManual(destinationState), destinationState, 'entrega');
    const additionalStopSnapshots = additionalStops
      .map((stop) =>
        stop.mode === 'existing'
          ? withStopMetadata(buildLocationSnapshotFromOption(locationOptions.find((option) => option.id === stop.optionId) || null), stop, 'entrega')
          : withStopMetadata(buildLocationSnapshotFromManual(stop), stop, 'entrega')
      )
      .filter((stop) => stop?.address);
    const taskSummary = summarizeStopActions(destinationSnapshot, additionalStopSnapshots);

    await onSubmit?.(
      {
        ...form,
        clientAddressState,
        newClientAddressSnapshot: previewClientAddressSnapshot,
        originMode: originState.mode,
        destinationMode: destinationState.mode,
        originState,
        destinationState,
        originSnapshot,
        destinationSnapshot,
        additionalStops,
        additionalStopSnapshots,
        serviceTask: taskSummary.serviceTask,
        serviceTaskLabel: taskSummary.serviceTaskLabel,
        commercialModel: effectiveCommercialModel,
        pricingRule: effectivePricingRule,
        salePrice: commercialPreview.estimatedTotalToCharge || 0,
        fixedMarginAmount:
          effectivePricingRule === 'fixed_margin'
            ? (form.useClientPricing ? Number(selectedContract?.fixedMarginAmount || 0) || 0 : Number(form.fixedMarginAmount || 0) || 0)
            : 0,
        marginPercent:
          effectivePricingRule === 'percentage_margin'
            ? (form.useClientPricing
                ? Number(selectedContract?.marginPercent || 0) || resolveMarginPresetPercent(selectedContract?.marginPreset || inferMarginPreset(selectedContract?.marginPercent))
                : effectiveMarginPreset === 'manual'
                  ? Number(form.marginPercent || 0) || 0
                  : resolveMarginPresetPercent(effectiveMarginPreset))
            : 0,
        marginPreset: effectivePricingRule === 'percentage_margin' ? effectiveMarginPreset : '',
        agreedPricePerTrip:
          effectivePricingRule === 'manual_total'
            ? (form.useClientPricing ? Number(selectedContract?.agreedPricePerTrip || 0) || 0 : Number(form.agreedPricePerTrip || 0) || 0)
            : 0,
      },
      selectedClient
    );
  };

  return (
    <div className="fixed inset-0 z-[1800] flex items-center justify-center bg-slate-950/60 px-4 py-4 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.28)]">
        <div className="border-b border-slate-200 bg-[linear-gradient(135deg,#0f172a_0%,#5b21b6_56%,#f5d0fe_100%)] px-6 py-5 text-white">
          <div className="flex items-start justify-between gap-4">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-fuchsia-100">Servicio</p>
              <h2 className="mt-2 text-2xl font-black tracking-tight">Nuevo servicio comercial</h2>
              <p className="mt-2 text-sm leading-6 text-fuchsia-50/90">
                Primero registramos quién contrata, qué servicio se vende y desde dónde sale hacia dónde va. Después lo pasamos a logística para ejecutarlo.
              </p>
            </div>
            <button type="button" onClick={saving ? undefined : onClose} className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-white transition hover:bg-white/16 disabled:cursor-not-allowed disabled:opacity-60" disabled={saving}>
              <Icon path="M6 18L18 6M6 6l12 12" className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-6">
            <SectionFrame
              eyebrow="Base"
              title="Cliente y servicio"
              description="Acá definimos quién contrata, qué servicio ofrecemos y si es una vez o recurrente."
              tone="slate"
            >
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
            <div className="xl:col-span-7">
            <InputBlock label="Cliente" helper="La empresa que contrata el servicio.">
              <div className="space-y-4 rounded-[24px] border border-slate-200 bg-white/90 p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="inline-flex rounded-[22px] border border-slate-200 bg-white p-1.5 shadow-sm">
                    <button
                      type="button"
                      onClick={() => setForm((current) => ({ ...current, clientMode: 'existing' }))}
                      className={`rounded-[18px] px-4 py-2.5 text-left text-xs font-semibold transition ${!isCreatingClient ? 'bg-cyan-600 text-white shadow-[0_10px_24px_rgba(8,145,178,0.28)]' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                      <span className="block">Elegir cliente</span>
                      <span className={`mt-1 block text-[10px] font-medium ${!isCreatingClient ? 'text-cyan-50/90' : 'text-slate-400'}`}>
                        Ya está en la base
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setForm((current) => ({
                          ...current,
                          clientMode: 'new',
                          clientId: '',
                        }))
                      }
                      className={`rounded-[18px] px-4 py-2.5 text-left text-xs font-semibold transition ${isCreatingClient ? 'bg-violet-600 text-white shadow-[0_10px_24px_rgba(124,58,237,0.28)]' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                      <span className="block">Crear cliente</span>
                      <span className={`mt-1 block text-[10px] font-medium ${isCreatingClient ? 'text-violet-50/90' : 'text-slate-400'}`}>
                        Lo guardamos en la base
                      </span>
                    </button>
                  </div>
                  <div className="grid min-w-[240px] grid-cols-2 gap-2">
                    <div className="rounded-2xl border border-violet-200 bg-violet-50 px-3 py-2 shadow-sm">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-violet-700">Con contrato</p>
                      <p className="mt-1 text-lg font-black tracking-tight text-violet-900">{clientStats.withContract}</p>
                    </div>
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 shadow-sm">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700">Sin contrato</p>
                      <p className="mt-1 text-lg font-black tracking-tight text-amber-900">{clientStats.withoutContract}</p>
                    </div>
                  </div>
                </div>

                {!isCreatingClient ? (
                  <div className={existingClientPanelClassName}>
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Base de clientes
                        </p>
                        <p className="mt-1 text-sm text-slate-500">
                          Elegí rápido y mirá enseguida si el cliente trabaja con contrato o como viaje puntual.
                        </p>
                      </div>
                      <span className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${selectedClient ? selectedClientAccent.badge : 'border-slate-200 bg-white text-slate-500'}`}>
                        {selectedClient ? clientContractLabel : 'Sin elegir'}
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                      <div className="rounded-2xl border border-violet-200 bg-violet-50 px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-violet-700">Contrato</p>
                        <p className="mt-1 text-xs font-bold text-violet-900">Cliente con acuerdo activo</p>
                      </div>
                      <div className="rounded-2xl border border-cyan-200 bg-cyan-50 px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-700">Por viaje</p>
                        <p className="mt-1 text-xs font-bold text-cyan-900">Cobro por servicio cerrado</p>
                      </div>
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Mensual</p>
                        <p className="mt-1 text-xs font-bold text-emerald-900">Acumula y cobra por período</p>
                      </div>
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700">Sin contrato</p>
                        <p className="mt-1 text-xs font-bold text-amber-900">Viaje puntual o random</p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <InputBlock label="Buscar cliente" helper="Escribí nombre, contacto o teléfono. La lista ya te marca si el cliente tiene contrato o entra como puntual.">
                        <input
                          value={clientSearch}
                          onChange={(event) => {
                            const value = event.target.value;
                            setClientSearch(value);
                            setForm((current) => ({ ...current, clientId: '' }));
                          }}
                          className={baseFieldClassName}
                          placeholder="Buscar cliente en la base"
                        />
                      </InputBlock>
                      {selectedClient ? (
                        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-[20px] border border-white/80 bg-white/80 px-4 py-3 shadow-sm">
                          <div className="min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Cliente elegido</p>
                            <p className="mt-1 truncate text-sm font-black tracking-tight text-slate-950">
                              {buildClientDisplayName(selectedClient)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setForm((current) => ({ ...current, clientId: '' }));
                              setClientSearch('');
                            }}
                            className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600 transition hover:bg-slate-50"
                          >
                            Cambiar
                          </button>
                        </div>
                      ) : null}
                      <div className="mt-3 space-y-2">
                        {filteredClients.length > 0 ? (
                          filteredClients.map((client) => {
                            const contractMeta = buildClientContractMeta(client);
                            const isSelected = form.clientId === client.id;
                            return (
                              <button
                                key={client.id}
                                type="button"
                                onClick={() => {
                                  setForm((current) => ({ ...current, clientId: client.id, clientMode: 'existing' }));
                                  setClientSearch(buildClientDisplayName(client));
                                }}
                                className={`flex w-full flex-col gap-3 rounded-[22px] border px-4 py-3 text-left shadow-sm transition hover:-translate-y-0.5 ${
                                  isSelected
                                    ? `${contractMeta.accent.shell} ring-2 ring-offset-2 ring-offset-white ${contractMeta.contract ? 'ring-violet-200' : 'ring-amber-200'}`
                                    : `${contractMeta.accent.shell} hover:shadow-[0_18px_30px_rgba(15,23,42,0.08)]`
                                }`}
                              >
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="truncate text-sm font-black tracking-tight text-slate-950">{buildClientDisplayName(client)}</p>
                                    <p className="mt-1 text-xs text-slate-500">
                                      {client?.contactName || 'Sin contacto cargado'}
                                      {client?.phone ? ` · ${client.phone}` : ''}
                                    </p>
                                  </div>
                                  <span className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${contractMeta.accent.badge}`}>
                                    {contractMeta.label}
                                  </span>
                                </div>
                                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                  <div className="rounded-2xl border border-white/80 bg-white/80 px-3 py-2">
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Tipo comercial</p>
                                    <p className="mt-1 text-xs font-bold text-slate-900">{contractMeta.label}</p>
                                  </div>
                                  <div className="rounded-2xl border border-white/80 bg-white/80 px-3 py-2">
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Uso recomendado</p>
                                    <p className="mt-1 text-xs font-bold text-slate-900">
                                      {contractMeta.contract ? 'Usa la lógica del cliente' : 'Viaje puntual sin acuerdo'}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            );
                          })
                        ) : (
                          <div className="rounded-[20px] border border-dashed border-slate-300 bg-white px-4 py-4 text-sm text-slate-500">
                            No encontramos clientes con esa búsqueda. Si querés, crealo como cliente nuevo.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 rounded-[24px] border border-violet-200 bg-[linear-gradient(135deg,#f5f3ff_0%,#ffffff_100%)] p-4 shadow-sm">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full border border-violet-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-800">
                        Cliente nuevo
                      </span>
                      <span className="text-xs text-slate-500">Se guarda en la base general</span>
                    </div>
                    <input
                      value={form.newClientName}
                      onChange={handleChange('newClientName')}
                      className={baseFieldClassName}
                      placeholder="Nombre del cliente o empresa"
                      required={isCreatingClient}
                    />
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <input
                        value={form.newClientContactName}
                        onChange={handleChange('newClientContactName')}
                        className={baseFieldClassName}
                        placeholder="Persona de contacto (opcional)"
                      />
                      <input
                        value={form.newClientPhone}
                        onChange={handleChange('newClientPhone')}
                        className={baseFieldClassName}
                        placeholder="Teléfono (opcional)"
                      />
                    </div>
                    <input
                      type="email"
                      value={form.newClientEmail}
                      onChange={handleChange('newClientEmail')}
                      className={baseFieldClassName}
                      placeholder="Correo electrónico (opcional)"
                    />
                    <EndpointSection
                      title="Dirección principal del cliente"
                      description="La guardamos en la ficha del cliente para usarla en toda la app, aunque este servicio después viaje a otros destinos."
                      badgeLabel="Ficha del cliente"
                      state={clientAddressState}
                      onChange={setClientAddressState}
                      locationOptions={locationOptions}
                      tone="client"
                      existingEmptyText="Elegí una dirección ya registrada o cargá la dirección principal del cliente con geolocalización."
                      addressHelper="Escribí la dirección del cliente. Si elegís una sugerencia, la geolocalización queda lista."
                      hideSharedBaseToggle
                      hideContactType
                    />
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-800 shadow-sm">
                      <p className="max-w-xl leading-5">
                        Guardá primero este cliente en la base general. Después el resto de la planilla se habilita con ese cliente ya listo para usar en toda la app.
                      </p>
                      <button
                        type="button"
                        onClick={handlePersistClient}
                        disabled={savingClientDraft || !String(form.newClientName || '').trim() || !previewClientAddressSnapshot?.address}
                        className="inline-flex items-center gap-2 rounded-2xl border border-emerald-300 bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Icon path={savingClientDraft ? 'M12 6v6l4 2.5' : 'M12 4.5v15m7.5-7.5h-15'} className="h-4 w-4" />
                        {savingClientDraft ? 'Guardando cliente...' : 'Guardar cliente'}
                      </button>
                    </div>
                  </div>
                )}
                {selectedClient && !isCreatingClient ? (
                  <div className={`relative overflow-hidden rounded-[24px] border px-4 py-4 shadow-sm ${selectedClientAccent.shell}`}>
                    <div className={`absolute -right-8 -top-8 h-24 w-24 rounded-full blur-2xl ${selectedClientAccent.glow}`} />
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Cliente y contrato en servicio</p>
                        <p className="mt-1 text-sm font-bold text-slate-900">{buildClientDisplayName(selectedClient)}</p>
                      </div>
                      <span
                        className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] ${selectedContract ? selectedClientAccent.badge : selectedClientAccent.badge}`}
                      >
                        {selectedContract
                          ? SERVICE_CONTRACT_MODEL_OPTIONS.find((option) => option.value === selectedContract?.commercialModel)?.label || 'Contrato'
                          : 'Sin contrato'}
                      </span>
                    </div>
                    <p className="mt-3 text-xs leading-5 text-slate-500">
                      {selectedContract
                        ? 'Este cliente ya tiene una lógica comercial cargada. La planilla la puede usar por defecto o ajustar solo para este viaje.'
                        : 'Este cliente entra como viaje puntual. La ganancia se define en esta planilla sin tocar la liquidación del transporte.'}
                    </p>
                    <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                      <div className="rounded-2xl border border-white/80 bg-white/80 px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Modo comercial</p>
                        <p className="mt-1 text-sm font-bold text-slate-900">{clientContractLabel}</p>
                      </div>
                      <div className="rounded-2xl border border-white/80 bg-white/80 px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Regla base</p>
                        <p className="mt-1 text-sm font-bold text-slate-900">{selectedContractRuleLabel}</p>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/80 bg-white/75 px-3 py-3">
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Gestión del acuerdo</p>
                        <p className="mt-1 text-xs text-slate-600">
                          {selectedContract
                            ? 'Este cliente ya tiene contrato. Si querés ajustar porcentajes, cobro mensual o datos administrativos, editá el acuerdo desde acá.'
                            : 'Este cliente todavía entra sin contrato. Si querés dejar un acuerdo recurrente, crealo desde este mismo bloque.'}
                        </p>
                      </div>
                      {typeof onOpenClientContractEditor === 'function' ? (
                        <button
                          type="button"
                          onClick={() => onOpenClientContractEditor(selectedClient, selectedContract || null)}
                          className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-semibold shadow-sm transition ${
                            selectedContract
                              ? 'border-violet-300 bg-violet-600 text-white hover:bg-violet-700'
                              : 'border-amber-300 bg-amber-500 text-white hover:bg-amber-600'
                          }`}
                        >
                          <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                          {selectedContract ? 'Editar contrato' : 'Crear contrato'}
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </InputBlock>
            </div>
            <div className={`xl:col-span-5 transition ${clientProfileReady ? 'opacity-100' : 'opacity-55'}`}>
              <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-1 ${clientProfileReady ? '' : 'pointer-events-none'}`}>
                <InputBlock
                  label="Modalidad"
                  helper={
                    isPunctualService
                      ? 'Un solo servicio, sin recurrencia. Se cobra por este viaje.'
                      : 'Definí si este servicio corre como viaje, acuerdo o abono.'
                  }
                >
                  <select value={form.serviceMode} onChange={handleChange('serviceMode')} className={baseFieldClassName}>
                    {SERVICE_MODE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </InputBlock>
                <InputBlock label="Transporte" helper="Esto define la liquidación y el costo operativo del recorrido.">
                  <select value={form.serviceTransport} onChange={handleChange('serviceTransport')} className={baseFieldClassName}>
                    {SERVICE_TRANSPORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </InputBlock>
                <InputBlock
                  label="Frecuencia"
                  helper={
                    isPunctualService
                      ? 'Puntual bloquea la recurrencia y deja este servicio como única vez.'
                      : 'Si no es única vez, lo podemos dejar también en Programados.'
                  }
                >
                  <select
                    value={form.frequency}
                    onChange={handleChange('frequency')}
                    className={baseFieldClassName}
                    disabled={isPunctualService}
                  >
                    {SERVICE_TEMPLATE_FREQUENCY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </InputBlock>
                <InputBlock label="Fecha del servicio">
                  <input type="date" value={form.scheduledDate} onChange={handleChange('scheduledDate')} className={baseFieldClassName} required />
                </InputBlock>
                <InputBlock label="Prioridad">
                  <select value={form.priority} onChange={handleChange('priority')} className={baseFieldClassName}>
                    {SERVICE_PRIORITY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </InputBlock>
              </div>
              {!clientProfileReady ? (
                <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-800 shadow-sm">
                  Primero dejá listo el cliente con su dirección principal. Apenas quede completo, se habilita esta parte del servicio.
                </div>
              ) : null}
            </div>
            </div>
            {isRecurring && !isPunctualService ? (
              <label className="mt-4 inline-flex items-center gap-3 rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm font-medium text-cyan-900">
                <input
                  type="checkbox"
                  checked={form.saveAsTemplate}
                  onChange={(event) => setForm((current) => ({ ...current, saveAsTemplate: event.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                />
                Guardar también en Programados para reutilizarlo y editarlo después
              </label>
            ) : null}
            </SectionFrame>

            <div className={`transition ${clientProfileReady ? 'opacity-100' : 'opacity-55'}`}>
            <SectionFrame
              eyebrow="Recorrido"
              title="Origen, destino y paradas"
              description="Armamos la hoja base del viaje. Si hay varios puntos, los sumamos acá y el costo se proyecta mejor."
              tone="cyan"
            >
            <div className={clientProfileReady ? '' : 'pointer-events-none'}>
            <div className="mb-5 rounded-[24px] border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-cyan-900 shadow-sm">
              <p className="font-semibold">Origen operativo por defecto</p>
              <p className="mt-1 text-cyan-800/90">
                {defaultOperationalOrigin?.label
                  ? `${defaultOperationalOrigin.label}${defaultOperationalOrigin.address ? ` · ${defaultOperationalOrigin.address}` : ''}`
                  : 'Si la empresa tiene base operativa configurada, se usa como punto de salida por defecto. Si este viaje arranca en otro lugar, lo podés cambiar.'}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <EndpointSection title="Origen" state={originState} onChange={setOriginState} locationOptions={locationOptions} tone="origin" />
              <div>
                <EndpointSection title="Destino" state={destinationState} onChange={setDestinationState} locationOptions={locationOptions} tone="destination" />
                <StopActionFields state={destinationState} onChange={setDestinationState} />
              </div>
            </div>
            <div className="mt-4 rounded-[26px] border border-amber-200 bg-[linear-gradient(135deg,#fffbeb_0%,#ffffff_100%)] p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-black tracking-tight text-slate-900">Destinos extra</p>
                  <p className="mt-1 text-sm text-slate-500">Si este servicio pasa por varios puntos, los agregamos ahora.</p>
                </div>
                <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-800">
                  Paradas adicionales
                </span>
                <button
                  type="button"
                  onClick={() => setAdditionalStops((current) => [...current, createAdditionalStopState()])}
                  className="inline-flex items-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-700"
                >
                  <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                  Agregar destino
                </button>
              </div>
              {additionalStops.length > 0 ? (
                <div className="mt-4 space-y-4">
                  {additionalStops.map((stop, index) => (
                    <div key={stop.id} className="rounded-[24px] border border-amber-200 bg-white/95 p-4 shadow-sm">
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-bold text-slate-900">Destino extra {index + 1}</p>
                          <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">Parada adicional</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setAdditionalStops((current) => current.filter((item) => item.id !== stop.id))}
                          className="inline-flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-100"
                        >
                          <Icon path="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
                          Quitar
                        </button>
                      </div>
                      <EndpointSection
                        title={`Parada ${index + 1}`}
                        state={stop}
                        onChange={(updater) =>
                          setAdditionalStops((current) =>
                            current.map((item) =>
                              item.id !== stop.id ? item : typeof updater === 'function' ? updater(item) : updater
                            )
                          )
                        }
                        locationOptions={locationOptions}
                        tone="destination"
                      />
                      <StopActionFields
                        state={stop}
                        title={`Acción en parada ${index + 1}`}
                        onChange={(updater) =>
                          setAdditionalStops((current) =>
                            current.map((item) =>
                              item.id !== stop.id
                                ? item
                                : typeof updater === 'function'
                                  ? updater(item)
                                  : updater
                            )
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 rounded-[22px] border border-dashed border-amber-300 bg-white px-4 py-4 text-sm text-slate-500">
                  Por ahora queda en origen + destino final. Si necesitás más paradas, las sumás desde acá.
                </div>
              )}
            </div>
            </div>
            </SectionFrame>
            </div>

            <div className={`transition ${clientProfileReady ? 'opacity-100' : 'opacity-55'}`}>
            <SectionFrame
              eyebrow="Comercial"
              title="Costo, ganancia y cobro"
              description="La liquidación base sale de logística. Acá definimos cómo gana la empresa y cómo se cobra."
              tone="violet"
            >
            <div className={`${clientProfileReady ? '' : 'pointer-events-none'} rounded-[26px] border px-5 py-5 shadow-sm ${selectedContract ? 'border-violet-200 bg-[linear-gradient(135deg,#f5f3ff_0%,#ffffff_100%)]' : 'border-amber-200 bg-[linear-gradient(135deg,#fffbeb_0%,#ffffff_100%)]'}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${selectedContract ? 'text-violet-700' : 'text-amber-700'}`}>
                    {selectedContract ? 'Contrato detectado' : 'Sin contrato'}
                  </p>
                  <h4 className="mt-2 text-xl font-black tracking-tight text-slate-950">
                    {selectedContract ? (selectedContract?.name || 'Acuerdo comercial activo') : 'Viaje puntual o cliente sin acuerdo'}
                  </h4>
                  <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                    {selectedContract
                      ? 'Este servicio toma por defecto la lógica comercial del cliente. Si hace falta, podés desactivar esa configuración y hacer un ajuste puntual solo para este viaje.'
                      : 'Este servicio vive sin contrato. La app toma el costo operativo del recorrido y te deja aplicar una regla comercial puntual para calcular ganancia y total a cobrar.'}
                  </p>
                </div>
                <span className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] ${selectedContract ? 'border-violet-200 bg-white text-violet-700' : 'border-amber-200 bg-white text-amber-700'}`}>
                  {activeCommercialModelMeta?.label || 'A definir'}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-[20px] border border-white/80 bg-white/90 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Modo de cobro</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">{activeCommercialModelMeta?.label || 'A definir'}</p>
                </div>
                <div className="rounded-[20px] border border-white/80 bg-white/90 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Regla comercial</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">
                    {effectiveCommercialModel === 'monthly_fixed' ? 'Abono fijo mensual' : activePricingRuleMeta?.label || 'Porcentaje sobre costo'}
                  </p>
                </div>
                <div className="rounded-[20px] border border-white/80 bg-white/90 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Origen comercial</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">
                    {selectedContract ? 'Contrato del cliente' : 'Configuración puntual del servicio'}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <InputBlock
                label="Modo de cobro"
                helper={
                  isPunctualService
                    ? 'En puntual, el cobro queda por viaje y se registra al cerrar el servicio.'
                    : 'Por viaje impacta al cerrar. Mensual acumula hasta registrar el cobro.'
                }
              >
                <select
                  value={effectiveCommercialModel}
                  onChange={(event) => setForm((current) => ({ ...current, commercialModel: event.target.value, useClientPricing: false }))}
                  disabled={isPunctualService || (form.useClientPricing && Boolean(selectedContract))}
                  className={baseFieldClassName}
                >
                  {SERVICE_CONTRACT_MODEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </InputBlock>
              <InputBlock label="Regla de ganancia" helper="Tomamos la del cliente por defecto, pero la podés ajustar.">
                <select
                  value={effectivePricingRule}
                  onChange={(event) => setForm((current) => ({ ...current, pricingRule: event.target.value, useClientPricing: false }))}
                  disabled={effectiveCommercialModel === 'monthly_fixed' || (form.useClientPricing && Boolean(selectedContract))}
                  className={baseFieldClassName}
                >
                  {SERVICE_PRICING_RULE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </InputBlock>
              {effectiveCommercialModel !== 'monthly_fixed' && effectivePricingRule === 'percentage_margin' ? (
                <InputBlock label="Nivel de precio" helper="Elegí rápido si querés una propuesta barata, normal, cara o manual.">
                  <select
                    value={effectiveMarginPreset}
                    onChange={handleMarginPresetChange}
                    disabled={form.useClientPricing && Boolean(selectedContract)}
                    className={baseFieldClassName}
                  >
                    {SERVICE_MARGIN_PRESET_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}{Number.isFinite(Number(option.percent)) ? ` · ${option.percent}%` : ''}
                      </option>
                    ))}
                  </select>
                </InputBlock>
              ) : null}
              {selectedContract ? (
                <label className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 shadow-sm">
                  <input
                    type="checkbox"
                    checked={form.useClientPricing}
                    onChange={(event) => setForm((current) => ({ ...current, useClientPricing: event.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  />
                  Usar configuración del cliente
                </label>
              ) : null}
              {effectiveCommercialModel !== 'monthly_fixed' && effectivePricingRule === 'percentage_margin' ? (
                effectiveMarginPreset === 'manual' ? (
                  <InputBlock label="Porcentaje de ganancia">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.useClientPricing ? Number(selectedContract?.marginPercent || 0) || '' : form.marginPercent}
                      onChange={handleChange('marginPercent')}
                      disabled={form.useClientPricing && Boolean(selectedContract)}
                      className={baseFieldClassName}
                      placeholder="40"
                    />
                  </InputBlock>
                ) : (
                  <InputBlock label="Porcentaje aplicado">
                    <input
                      type="text"
                      value={`${form.useClientPricing
                        ? Number(selectedContract?.marginPercent || 0) || resolveMarginPresetPercent(selectedContract?.marginPreset || inferMarginPreset(selectedContract?.marginPercent))
                        : resolveMarginPresetPercent(effectiveMarginPreset)}%`}
                      readOnly
                      className={`${baseFieldClassName} bg-slate-50 text-slate-600`}
                    />
                  </InputBlock>
                )
              ) : null}
              {effectiveCommercialModel !== 'monthly_fixed' && effectivePricingRule === 'fixed_margin' ? (
                <InputBlock label="Ganancia fija por viaje">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.useClientPricing ? Number(selectedContract?.fixedMarginAmount || 0) || '' : form.fixedMarginAmount}
                    onChange={handleChange('fixedMarginAmount')}
                    disabled={form.useClientPricing && Boolean(selectedContract)}
                    className={baseFieldClassName}
                    placeholder="0"
                  />
                </InputBlock>
              ) : null}
              {effectiveCommercialModel !== 'monthly_fixed' && effectivePricingRule === 'manual_total' ? (
                <InputBlock label="Total manual por viaje">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.useClientPricing ? Number(selectedContract?.agreedPricePerTrip || 0) || '' : form.agreedPricePerTrip}
                    onChange={handleChange('agreedPricePerTrip')}
                    disabled={form.useClientPricing && Boolean(selectedContract)}
                    className={baseFieldClassName}
                    placeholder="0"
                  />
                </InputBlock>
              ) : null}
            </div>

            <div className="mt-5 rounded-[24px] border border-white/80 bg-white/80 p-4 shadow-sm">
              <InputBlock label="Observación" helper="Instrucciones comerciales o contexto operativo útil para la ejecución.">
                <textarea value={form.notes} onChange={handleChange('notes')} className={`${baseFieldClassName} min-h-[110px] resize-y`} placeholder="Ejemplo: retiro con horario fijo, espera en puerta, servicio parcial o entrega en depósito tercero." />
              </InputBlock>
            </div>
            </SectionFrame>
            </div>

            <SectionFrame
              eyebrow="Lectura"
              title="Resumen automático"
              description="Acá vemos rápido qué estamos armando antes de guardarlo."
              tone="emerald"
            >
            <div className="rounded-[24px] border border-white/80 bg-white/85 px-5 py-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Lectura rápida</p>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Cliente</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">
                    {selectedClient
                      ? buildClientDisplayName(selectedClient)
                      : isCreatingClient
                        ? (String(form.newClientName || '').trim() || 'Completá el cliente nuevo')
                        : 'Todavía sin cliente elegido'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Origen</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">{originState.mode === 'existing' ? (locationOptions.find((option) => option.id === originState.optionId)?.label || 'Sin origen elegido') : (originState.label || originState.address || 'Origen manual')}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Destino</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">{destinationState.mode === 'existing' ? (locationOptions.find((option) => option.id === destinationState.optionId)?.label || 'Sin destino elegido') : (destinationState.label || destinationState.address || 'Destino manual')}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Frecuencia</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">
                    {SERVICE_TEMPLATE_FREQUENCY_OPTIONS.find((option) => option.value === form.frequency)?.label || 'Única vez'}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Costo transporte</p>
                <p className="mt-2 text-2xl font-black tracking-tight text-slate-950">{new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(Number(commercialPreview.estimatedOperationalCost || 0))}</p>
                <p className="mt-1 text-xs text-slate-500">Sale de la liquidación configurada para el transporte y del recorrido armado.</p>
              </div>
              <div className="rounded-[22px] border border-violet-200 bg-violet-50 px-4 py-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-violet-700">Ganancia</p>
                <p className="mt-2 text-2xl font-black tracking-tight text-violet-950">{new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(Number(commercialPreview.estimatedMargin || 0))}</p>
                <p className="mt-1 text-xs text-violet-700/80">Según la configuración del cliente o el ajuste puntual de esta planilla.</p>
              </div>
              <div className="rounded-[22px] border border-emerald-200 bg-emerald-50 px-4 py-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700">Total a cobrar</p>
                <p className="mt-2 text-2xl font-black tracking-tight text-emerald-950">{new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(Number(commercialPreview.estimatedTotalToCharge || 0))}</p>
                <p className="mt-1 text-xs text-emerald-700/80">Este total recién impacta en balance cuando el viaje se completa o cuando se cobra el mes.</p>
              </div>
            </div>
            </SectionFrame>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
            <button type="button" onClick={onClose} disabled={saving} className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60">
              Cancelar
            </button>
            <button type="submit" disabled={saving || !clientProfileReady} className="inline-flex items-center gap-2 rounded-2xl border border-violet-300 bg-violet-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60">
              <Icon path={saving ? 'M12 6v6l4 2.5' : 'M12 4.5v15m7.5-7.5h-15'} className="h-4 w-4" />
              {saving ? 'Guardando servicio...' : 'Guardar servicio'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceCreateModal;
